import {
  INotificationsRequestDTO,
  IPaginatedNotificationsDTO,
  INotificationMessageDTO,
  INotificationDTO,
  IUpdateNotificationRequestDTO,
  IDeleteNotificationRequestDTO,
  ICreateNotificationRequestDTO,
  IDownloadNotificationAttachmentDTO,
} from 'src/api/dtos';
import http from 'src/api/http';
import CommonRepository from 'src/api/repositories/common/common.repository';
import api from 'src/api/rtk/rtk-api';
import { IListQueryResult, IQueryResult } from 'src/api/types';
import { getFileNameFromAxiosHeader } from 'src/shared/utils';

export default class NotificationsRepository extends CommonRepository {
  private static readonly baseRoute = '/notifications';
  private static readonly apiURL = this.setBaseUrl(this.baseURL, this.baseRoute);

  public upload = async (payload: FormData): Promise<void> => {
    try {
      const { data } = await http.post(`${NotificationsRepository.apiURL}/attachments`, payload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return data;
    } catch (e) {
      return Promise.reject(e);
    }
  };

  public download = async ({
    notificationId,
    attachmentId,
  }: IDownloadNotificationAttachmentDTO): Promise<{ stream: Blob; fileName?: string }> => {
    try {
      const { data, headers } = await http.get(
        `${NotificationsRepository.apiURL}/${notificationId}/attachments/${attachmentId}`,
        {
          responseType: 'blob',
        },
      );

      return {
        stream: data,
        fileName: getFileNameFromAxiosHeader(headers),
      };
    } catch (e) {
      return Promise.reject(e);
    }
  };

  public static endpoints = api.injectEndpoints({
    overrideExisting: true,
    endpoints: (build) => ({
      getNotifications: build.query<IPaginatedNotificationsDTO, INotificationsRequestDTO>({
        query: (params) => {
          return { url: this.apiURL, method: 'get', params };
        },
        providesTags: [{ type: 'Notifications', id: 'Notifications' }],
      }),
      getNotification: build.query<INotificationMessageDTO, number>({
        query: (id) => {
          return { url: `${this.apiURL}/${id}`, method: 'get' };
        },
        transformResponse: (response: IQueryResult<INotificationMessageDTO>) => response.item,
        providesTags: [{ type: 'Notifications', id: 'Notification' }],
        keepUnusedDataFor: 0,
      }),
      updateNotifications: build.mutation<INotificationDTO[], IUpdateNotificationRequestDTO>({
        query: (data) => {
          return { url: this.apiURL, method: 'patch', data };
        },
        transformResponse: (response: IListQueryResult<INotificationDTO>) => response.items,
        invalidatesTags: ['Notifications'],
      }),
      deleteNotifications: build.mutation<void, IDeleteNotificationRequestDTO>({
        query: (data) => {
          return { url: this.apiURL, method: 'delete', data };
        },
        invalidatesTags: ['Notifications'],
      }),
      createNotification: build.mutation<void, ICreateNotificationRequestDTO>({
        query: (data) => {
          return { url: this.apiURL, method: 'post', data };
        },
        invalidatesTags: ['Notifications'],
      }),
      getNotificationsUnreadCount: build.query<number, void>({
        query: () => {
          return { url: `${this.apiURL}/unreadcount`, method: 'get' };
        },
        transformResponse: (response: IQueryResult<number>) => response.item,
        keepUnusedDataFor: 0,
        providesTags: [{ type: 'Notifications', id: 'UnreadCount' }],
      }),
    }),
  });
}
